import React, {ReactElement, useEffect} from 'react';

import classNames from 'classnames';
import {connect} from 'react-redux';

import {useFeature} from 'components/feature';
import {PURCHASE_STORAGE} from 'constants/analytics';
import {PlacingOrderScreen} from 'desktop/components/placingOrderScreen/placingOrderScreen';
import {ApplicationStoreType} from 'desktop/store';
import {sessionStore} from 'libs/storage/storage';
import {PaymentMethodsType} from 'types/PaymentMethodsType';

import {PAYMENTS, STANDARD_PAYMENT_METHODS} from '../../constants/paymentStep';
import {usePreloadPrimer} from '../../hooks/primer/usePrimerPreload';
import {useSetDefaultPayment} from '../../hooks/useSetDefaultPayment/useSetDefaultPayment';
import {
    getIsCompleteOrderPlacing,
    getIsPlacingOrder,
} from '../../store/selectors/completeOrder/completeOrder';
import {
    getIsPaymentMethodsLoading,
    getPaymentMethods,
} from '../../store/selectors/paymentStep/paymentStep';

import {BnPlOfferPopupConnected} from '../bnplOfferPopup/bnplOfferPopup';
import {PaymentDefaultConnected} from '../paymentDefault/paymentDefault';

import {PAYMENTS_COMPONENTS} from './paymentsComponents';

import {PaymentsLoader} from './paymentsLoader/paymentsLoader';
import {usePaymentStepAnalytics} from './usePaymentStepAnalytics';

import s from './paymentsMethods.scss';

export type PropsType = {
    methods: PaymentMethodsType;
    isPaymentLoading: boolean;
    isPlacingOrder: boolean;
    isPlacingOrderPrimer: boolean;
};

/**
 * Payments Methods for checkout
 *
 * @constructor
 */
export const PaymentsMethods: React.FC<PropsType> = ({
    isPaymentLoading,
    isPlacingOrder,
    isPlacingOrderPrimer,
    methods,
}) => {
    const isFacelift = useFeature('dCheckoutFacelift');

    const isCheckoutInsufficientFundsOffer = useFeature('checkoutInsufficientFundsOffer');
    const methodKeys = Object.keys(methods.list).sort(
        (prev, current) =>
            Number(methods.list[prev as PAYMENTS]?.order) -
            Number(methods.list[current as PAYMENTS]?.order),
    );

    const filteredMethods = methodKeys.map(payment => {
        let content: ReactElement | null;
        if (STANDARD_PAYMENT_METHODS.includes(payment as PAYMENTS))
            content = <PaymentDefaultConnected key={payment} paymentKey={payment as PAYMENTS} />;
        else content = PAYMENTS_COMPONENTS[payment as PAYMENTS] || null;

        return {
            content,
            paymentCode: payment,
        };
    });

    usePreloadPrimer();
    usePaymentStepAnalytics();
    useSetDefaultPayment(filteredMethods);

    useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            // for modern browsers to prevent navigating away while placing order
            event.preventDefault();

            // prevent navigating away for legacy cases
            return true;
        };
        if (isPlacingOrder) {
            window.addEventListener('beforeunload', handleBeforeUnload);
        } else {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        }
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            // need this for primer credit card;
            window.onbeforeunload = null;
        };
    }, [isPlacingOrder]);

    useEffect(() => {
        sessionStore.set(PURCHASE_STORAGE, 'Checkout');
    }, []);

    return (
        <div
            aria-label="Payments methods list"
            className={classNames(s.wrapper, {[s.wrapperFaceLift]: isFacelift})}
        >
            {isPaymentLoading && <PaymentsLoader />}
            {filteredMethods.map(m => m.content)}
            {isCheckoutInsufficientFundsOffer && Object.keys(methods).length !== 0 && (
                <BnPlOfferPopupConnected />
            )}
            {isPlacingOrderPrimer && <PlacingOrderScreen />}
        </div>
    );
};

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @returns {PropsType} props
 */
const mapStateToProps = (store: ApplicationStoreType) => ({
    isPaymentLoading: getIsPaymentMethodsLoading(store),
    isPlacingOrder: getIsPlacingOrder(store),
    isPlacingOrderPrimer: getIsCompleteOrderPlacing(store),
    methods: getPaymentMethods(store),
});

export const PaymentsMethodsConnected = connect(mapStateToProps)(PaymentsMethods);
